import { useQuery, useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { axiosInstance, printError, useUtilities } from "helpers";
import {
  addSingleCompany,
  getAllCompanies,
  updateSingleCompany,
  deleteSingleCompany,
} from "redux/reducers/usersModule/companies";
import {
  unLoadDefaultMembers,
  unuploadMembers,
} from "redux/reducers/usersModule/members";
import { queryClient } from "client";
import { t } from "i18next";

async function getCompanies() {
  return axiosInstance
    .get("/allCompanies", {
      params: {
        api_key: localStorage.getItem("apikey"),
        added_by: localStorage.getItem("user_id"),
      },
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

function useGetCompanies() {
  return useQuery(["companies"], getCompanies);
}

export function useCompanies() {
  const { data, isLoading, error, isRefetching, isStale } = useGetCompanies();
  const {
    dispatch,
    companies: { companies, loaded },
  } = useUtilities();

  useEffect(() => {
    if (isLoading || loaded || isRefetching || isStale) return;
    if (data) dispatch(getAllCompanies(data?.data || []));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, loaded, isRefetching, isStale]);
  return {
    data: companies,
    isLoading: isLoading || !loaded,
    isRefetching,
    isStale,
    error,
  };
}

async function addCompany(company) {
  return axiosInstance
    .post("addCompany", {
      api_key: localStorage.getItem("apikey"),
      added_by: localStorage.getItem("user_id"),
      ...company,
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useAddCompany(redirect = "") {
  const { dispatch, navigate, notification } = useUtilities();
  return useMutation(addCompany, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(addSingleCompany(data.data));
        notification({
          type: "success",
          message: t("users.companies.api.notification.companyAdded")
        });
        if (redirect) navigate(redirect + data.data.id);
        queryClient.invalidateQueries(["siterelatedusers"]);
        dispatch(unLoadDefaultMembers());
        queryClient.invalidateQueries(["members"]);
        dispatch(unuploadMembers());
      } else {
        notification({
          type: "error",
          message: printError(data.message),
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: printError(err.message),
      });
    },
  });
}

async function updateCompany(company) {
  return axiosInstance
    .post("updateCompany", {
      api_key: localStorage.getItem("apikey"),
      added_by: localStorage.getItem("user_id"),
      ...company,
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useUpdateCompany() {
  const { dispatch, notification } = useUtilities();
  return useMutation(updateCompany, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(updateSingleCompany(data.data));
        notification({
          type: "success",
          message: t("users.companies.api.notification.companyUpdated"),
        });
        queryClient.invalidateQueries(["siterelatedusers"]);
        dispatch(unLoadDefaultMembers());
        queryClient.invalidateQueries(["members"]);
        dispatch(unuploadMembers());
      } else {
        notification({
          type: "error",
          message: printError(data.message || data.message),
        });
      }
    },
    onError: (err) => {
      notification({
        type: "error",
        message: printError(err.message),
      });
    },
  });
}

async function deleteCompany(id) {
  return axiosInstance
    .post("delCompany", {
      api_key: localStorage.getItem("apikey"),
      company_id: id,
    })
    .then((res) => ({ ...res.data, id }))
    .catch((err) => new Error(err));
}

export function useDeleteCompany(redirect = "") {
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(deleteCompany, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(deleteSingleCompany(data.id));
        notification({
          type: "success",
          message: t("users.companies.api.notification.companyDeleted")
        });
        if (redirect) navigate(redirect);
        queryClient.invalidateQueries(["siterelatedusers"]);
        dispatch(unLoadDefaultMembers());
        queryClient.invalidateQueries(["members"]);
        dispatch(unuploadMembers());
      } else {
        notification({
          type: "error",
          message: printError(data.message || data.message),
        });
      }
    },
    onError: (err) => {
      dispatch({
        type: "error",
        message: printError(err.message),
      });
    },
  });
}
